import { IElementBaseModel } from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import { ITextBoxNode } from '../../slide-content-enumerator/enumerator-types.interface';
import { IRect } from '@src/slide/infrastructure/module/independent/graphic/rect/rect';
import { ContentTree, ContentTreeNode } from '../../slide-content-layout-analyzer/core/algorithm/data-structure/content-tree';
import { TextLeafNode } from '../../slide-content-layout-analyzer/core/algorithm/data-structure/text-leaf-node';
import { BaseAnalyzer } from '../base/base-analyzer';
import { MachineContentLayoutAnalyzer } from './machine-content-layout-analyzer';
import { MachineChartAnalyzer } from './machine-chart-analyzer';
import { MachineImageAnalyzer } from './machine-image-analyzer';
import { TextAlignmentType } from '@tencent/alloy-editor';
import { SlideInterface } from '@tencent/docs-api';

type IResult = SlideInterface.ITextReview;

interface ITextReview extends SlideInterface.TextLineReview {
    element?: IElementBaseModel,
}

export interface IParagraphReview extends SlideInterface.ParagraphReview {
    textLines: ITextReview[];
    mostFontSize: number,
    textLeaf?: TextLeafNode,
    align?: TextAlignmentType,
    element?: IElementBaseModel,
    rect?: IRect,
}

export interface IPageReview extends SlideInterface.PageReview {
    title: IParagraphReview;
    bodyTexts: IParagraphReview[];
    pageLeafs: TextLeafNode[],
}

export interface IReview extends IResult {
    pages: IPageReview[],
}

export class MachineReviewAnalyzer extends BaseAnalyzer<IResult> {
    private reviewResult: IReview;

    public constructor(
        private contentLayoutAnalyzer: MachineContentLayoutAnalyzer,
        private imageAnalyzer?: MachineImageAnalyzer,
        private chartAnalyzer?: MachineChartAnalyzer,
    ) {
        super();
    }

    /**
     * 含有中间计算值的分析结果
     */
    public get detailedReview(): IReview {
        return this.reviewResult;
    }

    public async calculateResult(presentation: IPresentationModel) {
        const contentTrees = await this.contentLayoutAnalyzer.result;
        const pageReviews = await Promise.all(this.analysisByContentTreeList(contentTrees));
        return {
            pages: pageReviews,
            pageCount: presentation.slideCount,
            pageWidth: presentation.props.slideWidth,
            pageHeight: presentation.props.slideHeight,
        };
    }

    private analysisByContentTreeList(contentTreeList: ContentTree[]) {
        return contentTreeList.map((contentTree, index) => this.analysisPageByLayout(contentTree.children[0], index));
    }

    private async analysisPageByLayout(
        contentTreeRoot: ContentTreeNode,
        index: number,
    ): Promise<IPageReview> {
        const imagesReview = await this.imageAnalyzer?.result;
        const chartsReview = await this.chartAnalyzer?.result;
        // 把 body 需要的信息提取出来
        const bodyReviews = this.getTextReviewFromTree(contentTreeRoot.getTextTree());
        return {
            title: this.getTitleWithParagraphs(bodyReviews),
            bodyTexts: bodyReviews,
            images: imagesReview?.[index] ?? [],
            charts: chartsReview?.[index] ?? [],
            pageLeafs: bodyReviews.map(i => i.textLeaf).filter(Boolean) as TextLeafNode[],
        };
    }

    /**
     * 和 textAnalyzer 的 analysisTitleInfo 一样的逻辑，但是 analysisTitleInfo 很难抽象出来给这里用。先单独写一个，之后考虑一下如何抽象算法
     */
    private getTextReviewFromTree(tree?: TextLeafNode | Array<TextLeafNode | TextLeafNode[]>): IParagraphReview[] {
        if (!tree) {
            return [];
        }
        // 单个节点
        if (!Array.isArray(tree)) {
            return [this.getParagraphReviewFromLeaf(tree)];
        }
        // 多个节点
        return tree.flatMap((node) => {
            if (!Array.isArray(node)) {
                return this.getParagraphReviewFromLeaf(node);
            }
            return this.getTextReviewFromTree(node);
        });
    }

    private getDefaultParagraphReview(): IParagraphReview {
        return { text: '', textLines: [], lineSpacing: 0, spaceAfter: 0, spaceBefore: 0, mostFontSize: 0 };
    }

    private getTitleWithParagraphs(paragraphs: IParagraphReview[]) {
        // 排除特定文本
        const isIgnore = (text: string) => {
            if (/^[0-9.,。，、\s]+$/.test(text)) {
                return true;
            }
        };

        return paragraphs.reduce((pre, cur) => {
            if (pre.mostFontSize > 0) {
                return pre;
            }
            if (!isIgnore(cur.text) && cur.text.length < 16) {
                return cur;
            }
            return pre;
        }, this.getDefaultParagraphReview());
    }

    private getParagraphReviewFromLeaf(leaf: TextLeafNode): IParagraphReview {
        const blocks = leaf.contentBlock.visualBlocks;
        if (blocks.length < 1) {
            return this.getDefaultParagraphReview();
        }
        // 叶子结点出现多个图形块（visualBlocks.length > 0），一般是文档结构过于复杂时出现，先忽略，只取第一个分析
        const [block] = blocks;
        const textDocument = (block.info as ITextBoxNode)?.textDocumentElement;
        const textPath = (block.info as ITextBoxNode)?.textBoxPath;
        if (!textDocument || textPath.length > 1) {
            console.error('template review unknown error');
        }

        const [textIndex] = textPath;
        const textReviews: ITextReview[] = textDocument.children[textIndex].children.map(textLine => ({
            text: textLine.text,
            lang: textLine.lang ?? '',
            ea: textLine.fonts?.ea ?? '',
            latin: textLine.fonts?.latin ?? '',
            font: textLine.fonts?.[textLine.lang] ?? '',
            fontSize: textLine.fontSize ?? 0,
            bold: (textLine.fontWeight as any) === 'bold',
            element: block.info?.element,
        }));
        const fontSizeRecord = textReviews.reduce<Map<number, number>>((res, cur) => {
            const { fontSize, text } = cur;
            if (!fontSize) {
                return res;
            }
            if (!res.has(fontSize)) {
                res.set(fontSize, text.length);
                return res;
            }
            res.set(fontSize, (res.get(fontSize) ?? 0) + text.length);
            return res;
        }, new Map());
        return {
            text: leaf.text,
            textLines: textReviews,
            mostFontSize: Array.from(fontSizeRecord.entries()).sort((a, b) => a[1] - b[1])[0]?.[0] ?? 0,
            lineSpacing: textDocument.children[textIndex].lineSpacing ?? 0,
            spaceAfter: textDocument.children[textIndex].spaceAfter ?? 0,
            spaceBefore: textDocument.children[textIndex].spaceBefore ?? 0,
            align: textDocument.children[textIndex].textAlign,
            element: block.info?.element,
            rect: block.rect,
            textLeaf: leaf,
        };
    }
}
